import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { invoicesApi, paypalApi, SettlementData, DirectDepositSettings } from '../utils/api';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { redirectTo } from '../utils/navigation';
import { adjustColor, createLightTint, resolveLocale } from '../utils/branding';
import { pickPayPalOrderId, buildPaymentReference, formatIban, sortByDisplayOrder } from '../utils/settlement';

// Default branding colors (Tailwind blue-600 and emerald-500)
const DEFAULT_PRIMARY_COLOR = '#2563EB';
const DEFAULT_ACCENT_COLOR = '#10B981';

export const Settlement: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const token = searchParams.get('token');

  // PayPal return parameters
  const paypalCancelled = searchParams.get('cancelled') === 'true';
  // PayPal return is detected by:
  // 1. Being on the /paypal-return route
  // 2. Having a PayerID in URL (added by PayPal)
  const isPayPalReturnRoute = location.pathname.includes('/paypal-return');
  const isPayPalReturn = isPayPalReturnRoute || searchParams.has('PayerID') || searchParams.has('payerId');

  const [settlementData, setSettlementData] = useState<SettlementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [showBankTransferDialog, setShowBankTransferDialog] = useState(false);
  const [bankTransferQrCode, setBankTransferQrCode] = useState<string | null>(null);
  const [bankTransferReference, setBankTransferReference] = useState<string>('');
  const [loadingQrCode, setLoadingQrCode] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [capturingPayPal, setCapturingPayPal] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Ref to prevent duplicate PayPal capture attempts
  const captureAttemptedRef = useRef(false);

  // Compute branding colors with fallbacks
  const brandingColors = useMemo(() => {
    const primary = settlementData?.tenant.primary_color || DEFAULT_PRIMARY_COLOR;
    const accent = settlementData?.tenant.accent_color || DEFAULT_ACCENT_COLOR;
    return {
      primary,
      accent,
      primaryHover: adjustColor(primary, -20),
      primaryLight: createLightTint(primary, 0.1),
    };
  }, [settlementData?.tenant.primary_color, settlementData?.tenant.accent_color]);

  // Fetch settlement data when invoice/token changes
  useEffect(() => {
    const fetchData = async () => {
      if (!invoiceId || !token) {
        setError(t('settlement.invalidSettlementLink'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await invoicesApi.getSettlementData(invoiceId, token);
        setSettlementData(data);
        setError('');
      } catch (err: any) {
        setError(err.message || t('settlement.failedToLoadInvoice'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [invoiceId, token, t]);

  // Handle PayPal return - capture the order (only once)
  useEffect(() => {
    if (isPayPalReturn && token && invoiceId && !paypalCancelled && settlementData && !captureAttemptedRef.current) {
      captureAttemptedRef.current = true;
      capturePayPalOrder();
    }
  }, [isPayPalReturn, token, invoiceId, paypalCancelled, settlementData]);

  const capturePayPalOrder = async () => {
    if (!token || !invoiceId) return;

    // PayPal returns the order ID as 'token' query parameter in the return URL
    // Try URL parameter first (more reliable), then fallback to localStorage
    const urlOrderId = searchParams.get('token');
    const storedOrderId = localStorage.getItem(`paypal_order_${invoiceId}`);

    // Note: PayPal's 'token' param is different from our settlement token
    // We need to check if the URL token looks like a PayPal order ID (starts with a letter/number, not hex)
    // Our settlement tokens are hex strings, PayPal order IDs are alphanumeric like "1AB23456CD789012E"
    const paypalOrderId = pickPayPalOrderId(storedOrderId, urlOrderId);

    if (!paypalOrderId) {
      setError(t('settlement.paypalOrderNotFound'));
      return;
    }

    setCapturingPayPal(true);
    try {
      const result = await paypalApi.captureOrder(invoiceId, paypalOrderId, token);

      // Clean up localStorage
      localStorage.removeItem(`paypal_order_${invoiceId}`);

      if (result.success) {
        setPaymentComplete(true);
      } else {
        setError(result.error || t('settlement.paymentCaptureFailed'));
      }
    } catch (err: any) {
      setError(err.message || t('settlement.failedToCapture'));
    } finally {
      setCapturingPayPal(false);
    }
  };

  const handlePayment = async () => {
    if (!invoiceId || !token || !selectedPaymentMethod) return;

    const selectedMethod = settlementData?.payment_methods.find(
      (m) => m.payment_method_id === selectedPaymentMethod
    );

    if (selectedMethod?.payment_method_name === 'paypal') {
      await handlePayPalCheckout();
      return;
    }

    if (selectedMethod?.payment_method_name === 'direct_deposit') {
      await handleBankTransfer(selectedMethod.settings);
      return;
    }
  };

  const handleBankTransfer = async (settings?: DirectDepositSettings) => {
    if (!invoiceId || !token || !selectedPaymentMethod || !settings) return;

    // Generate payment reference
    const paymentReference = buildPaymentReference(
      settings.reference_prefix || '',
      settlementData?.invoice.invoice_number || '',
      settlementData?.invoice.invoice_code || ''
    );
    setBankTransferReference(paymentReference);

    // Show bank transfer dialog
    setShowBankTransferDialog(true);

    // Fetch QR code if enabled
    if (settings.qr_code_enabled) {
      setLoadingQrCode(true);
      try {
        const result = await invoicesApi.getEpcQrCode(invoiceId, token, selectedPaymentMethod);
        setBankTransferQrCode(result.qr_code);
        setBankTransferReference(result.payment_reference);
      } catch (err: any) {
        console.error('Failed to generate QR code:', err);
        // Continue without QR code
      } finally {
        setLoadingQrCode(false);
      }
    }
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handlePayPalCheckout = async () => {
    if (!invoiceId || !token || !selectedPaymentMethod) return;

    setProcessing(true);

    try {
      const result = await paypalApi.createOrder(
        invoiceId,
        selectedPaymentMethod,
        token
        // return_url and cancel_url will use defaults from backend
      );

      // Store PayPal order ID in localStorage for capture after return
      localStorage.setItem(`paypal_order_${invoiceId}`, result.order_id);

      // Redirect to PayPal for approval
      redirectTo(result.approval_url);
    } catch (err: any) {
      alert(err.message || t('settlement.failedToCreatePayPal'));
      setProcessing(false);
    }
  };

  // Locale-aware formatting
  const currentLocale = resolveLocale(i18n.language);

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat(currentLocale, {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(currentLocale);
  };

  if (loading || capturingPayPal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto"
            style={{ borderColor: brandingColors.primary }}
          ></div>
          <p className="mt-4 text-gray-500">
            {capturingPayPal ? t('settlement.processingPayPal') : t('settlement.loadingInvoice')}
          </p>
        </div>
      </div>
    );
  }

  // Show message if PayPal was cancelled
  if (paypalCancelled && !paymentComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-yellow-600 text-5xl mb-4">!</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('settlement.paymentCancelled')}</h1>
          <p className="text-gray-600 mb-6">
            {t('settlement.paymentCancelledMessage')}
          </p>
          <button
            onClick={() => window.location.href = `/settlement/${invoiceId}?token=${token}`}
            className="text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            style={{
              backgroundColor: brandingColors.primary,
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = brandingColors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = brandingColors.primary}
          >
            {t('settlement.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (error || !settlementData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-600 text-5xl mb-4">&#9888;&#65039;</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('common.error')}</h1>
          <p className="text-gray-600">{error || t('settlement.invoiceNotFound')}</p>
        </div>
      </div>
    );
  }

  if (paymentComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-5xl mb-4" style={{ color: brandingColors.accent }}>&#10003;</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('settlement.paymentSuccessful')}</h1>
          <p className="text-gray-600 mb-6">
            {t('settlement.paymentSuccessMessage', { invoiceNumber: settlementData.invoice.invoice_number })}
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <div className="flex justify-between py-2">
              <span className="text-gray-600">{t('settlement.invoice')}</span>
              <span className="font-medium">{settlementData.invoice.invoice_number}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">{t('settlement.amountPaid')}</span>
              <span className="font-medium" style={{ color: brandingColors.accent }}>
                {formatCurrency(
                  settlementData.invoice.amount_due,
                  settlementData.invoice.currency,
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4">
        <div className="bg-white rounded-lg shadow-lg">
          <LanguageSwitcher />
        </div>
      </div>

      <div className="max-w-3xl mx-auto">
        {/* Header with tenant branding */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div
            className="px-6 py-8 text-white"
            style={{ backgroundColor: brandingColors.primary }}
          >
            {settlementData.tenant.logo_url && (
              <img
                src={settlementData.tenant.logo_url}
                alt={settlementData.tenant.tenant_name}
                className="h-12 mb-4"
              />
            )}
            <h1 className="text-3xl font-bold">{settlementData.tenant.tenant_name}</h1>
            <p className="mt-2 opacity-80">{t('settlement.paymentRequest')}</p>
          </div>

          {/* Invoice Details */}
          <div className="px-6 py-6 space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {t('settlement.invoiceDetails')}
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">{t('settlement.invoiceNumber')}</p>
                  <p className="text-base font-medium text-gray-900">
                    {settlementData.invoice.invoice_number}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('settlement.date')}</p>
                  <p className="text-base font-medium text-gray-900">
                    {formatDate(settlementData.invoice.created_at)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('settlement.status')}</p>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                    {settlementData.invoice.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('settlement.currency')}</p>
                  <p className="text-base font-medium text-gray-900">
                    {settlementData.invoice.currency}
                  </p>
                </div>
              </div>
            </div>

            {/* Billing Information */}
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {t('settlement.billingInfo')}
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                {settlementData.invoice.billing_info.company_name && (
                  <p className="text-sm">
                    <span className="font-medium">{t('settlement.company')}</span>{' '}
                    {settlementData.invoice.billing_info.company_name}
                  </p>
                )}
                <p className="text-sm">
                  <span className="font-medium">{t('settlement.contact')}</span>{' '}
                  {settlementData.invoice.billing_info.contact_name}
                </p>
                <p className="text-sm">
                  {settlementData.invoice.billing_info.address_line1}
                  {settlementData.invoice.billing_info.address_line2 && (
                    <>, {settlementData.invoice.billing_info.address_line2}</>
                  )}
                </p>
                <p className="text-sm">
                  {settlementData.invoice.billing_info.city}
                  {settlementData.invoice.billing_info.state && (
                    <>, {settlementData.invoice.billing_info.state}</>
                  )}{' '}
                  {settlementData.invoice.billing_info.postal_code}
                </p>
                <p className="text-sm">{settlementData.invoice.billing_info.country}</p>
                {settlementData.invoice.billing_info.tax_id && (
                  <p className="text-sm">
                    <span className="font-medium">{t('settlement.taxId')}</span>{' '}
                    {settlementData.invoice.billing_info.tax_id}
                  </p>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {t('settlement.contactInfo')}
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-sm">
                  <span className="font-medium">{t('settlement.email')}</span>{' '}
                  {settlementData.invoice.contact_info.email}
                </p>
                {settlementData.invoice.contact_info.phone && (
                  <p className="text-sm">
                    <span className="font-medium">{t('settlement.phone')}</span>{' '}
                    {settlementData.invoice.contact_info.phone}
                  </p>
                )}
              </div>
            </div>

            {/* Line Items */}
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('settlement.lineItems')}</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('settlement.description')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('settlement.qty')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('settlement.unitPrice')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('settlement.amount')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {settlementData.invoice.line_items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {item.description}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">
                          {formatCurrency(
                            item.unit_price,
                            settlementData.invoice.currency,
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                          {formatCurrency(item.amount, settlementData.invoice.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {settlementData.invoice.tax_rate ? (
                      <>
                        <tr className="bg-gray-50">
                          <td colSpan={3} className="px-4 py-3 text-right text-sm text-gray-600">
                            {settlementData.invoice.prices_include_tax ? t('settlement.subtotalExclTax') : t('settlement.subtotal')}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                            {formatCurrency(
                              settlementData.invoice.line_items.reduce(
                                (sum, item) => {
                                  if (settlementData.invoice.prices_include_tax && item.tax_amount) {
                                    return sum + (item.amount - item.tax_amount);
                                  }
                                  return sum + item.amount;
                                },
                                0
                              ),
                              settlementData.invoice.currency,
                            )}
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td colSpan={3} className="px-4 py-3 text-right text-sm text-gray-600">
                            {t('settlement.tax', { name: settlementData.invoice.tax_rate.tax_rate_name, rate: settlementData.invoice.tax_rate.rate_percentage })}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                            {formatCurrency(
                              settlementData.invoice.line_items.reduce(
                                (sum, item) => sum + (item.tax_amount || 0),
                                0
                              ),
                              settlementData.invoice.currency,
                            )}
                          </td>
                        </tr>
                        <tr className="bg-gray-100">
                          <td colSpan={3} className="px-4 py-4 text-right text-base font-bold text-gray-900">
                            {t('settlement.total')}
                          </td>
                          <td className="px-4 py-4 text-right text-lg font-bold text-gray-900">
                            {formatCurrency(
                              settlementData.invoice.amount,
                              settlementData.invoice.currency,
                            )}
                          </td>
                        </tr>
                        {settlementData.invoice.total_paid > 0 && (
                          <>
                            <tr className="bg-green-50">
                              <td colSpan={3} className="px-4 py-2 text-right text-sm text-green-700">
                                {t('settlement.alreadyPaid')}
                              </td>
                              <td className="px-4 py-2 text-right text-sm text-green-700">
                                -{formatCurrency(settlementData.invoice.total_paid, settlementData.invoice.currency)}
                              </td>
                            </tr>
                            <tr className="bg-blue-50">
                              <td colSpan={3} className="px-4 py-3 text-right text-base font-bold text-blue-900">
                                {t('settlement.remainingBalance')}
                              </td>
                              <td className="px-4 py-3 text-right text-lg font-bold text-blue-900">
                                {formatCurrency(settlementData.invoice.amount_due, settlementData.invoice.currency)}
                              </td>
                            </tr>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <tr className="bg-gray-50">
                          <td colSpan={3} className="px-4 py-4 text-right text-base font-bold text-gray-900">
                            {t('settlement.total')}
                          </td>
                          <td className="px-4 py-4 text-right text-lg font-bold text-gray-900">
                            {formatCurrency(
                              settlementData.invoice.amount,
                              settlementData.invoice.currency,
                            )}
                          </td>
                        </tr>
                        {settlementData.invoice.total_paid > 0 && (
                          <>
                            <tr className="bg-green-50">
                              <td colSpan={3} className="px-4 py-2 text-right text-sm text-green-700">
                                {t('settlement.alreadyPaid')}
                              </td>
                              <td className="px-4 py-2 text-right text-sm text-green-700">
                                -{formatCurrency(settlementData.invoice.total_paid, settlementData.invoice.currency)}
                              </td>
                            </tr>
                            <tr className="bg-blue-50">
                              <td colSpan={3} className="px-4 py-3 text-right text-base font-bold text-blue-900">
                                {t('settlement.remainingBalance')}
                              </td>
                              <td className="px-4 py-3 text-right text-lg font-bold text-blue-900">
                                {formatCurrency(settlementData.invoice.amount_due, settlementData.invoice.currency)}
                              </td>
                            </tr>
                          </>
                        )}
                      </>
                    )}
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Pending payment warning */}
            {settlementData.invoice.pending_amount > 0 && (
              <div className="border-t border-gray-200 pt-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-yellow-800">
                    ⚠️ {t('settlement.pendingWarningTitle')}
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    {t('settlement.pendingWarningBody', {
                      amount: formatCurrency(settlementData.invoice.pending_amount, settlementData.invoice.currency),
                    })}
                  </p>
                </div>
              </div>
            )}

            {/* Payment Methods */}
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {t('settlement.selectPaymentMethod')}
              </h2>
              <div className="space-y-3">
                {settlementData.payment_methods.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    {t('settlement.noPaymentMethods')}
                  </p>
                ) : (
                  sortByDisplayOrder(settlementData.payment_methods)
                    .map((method) => {
                      const isSelected = selectedPaymentMethod === method.payment_method_id;
                      return (
                        <label
                          key={method.payment_method_id}
                          className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50"
                          style={{
                            borderColor: isSelected ? brandingColors.primary : '#e5e7eb',
                            backgroundColor: isSelected ? brandingColors.primaryLight : undefined,
                          }}
                        >
                          <input
                            type="radio"
                            name="payment_method"
                            value={method.payment_method_id}
                            checked={isSelected}
                            onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                            className="h-4 w-4"
                            style={{ accentColor: brandingColors.primary }}
                          />
                          <div className="ml-4 flex-1">
                            <p className="text-base font-medium text-gray-900">
                              {method.display_name}
                            </p>
                            {method.description && (
                              <p className="text-sm text-gray-500">{method.description}</p>
                            )}
                          </div>
                        </label>
                      );
                    })
                )}
              </div>
            </div>

            {/* Payment Button */}
            <div className="border-t border-gray-200 pt-6">
              <button
                onClick={handlePayment}
                disabled={!selectedPaymentMethod || processing}
                className="w-full text-white px-6 py-4 rounded-lg font-semibold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: brandingColors.primary }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = brandingColors.primaryHover;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = brandingColors.primary;
                }}
              >
                {processing ? t('common.processing') : t('settlement.pay', { amount: formatCurrency(settlementData.invoice.amount_due, settlementData.invoice.currency) })}
              </button>
            </div>
          </div>
        </div>

        {/* Bank Transfer Instructions Dialog */}
        {showBankTransferDialog && (() => {
          const selectedMethod = settlementData.payment_methods.find(
            (m) => m.payment_method_id === selectedPaymentMethod
          );
          const settings = selectedMethod?.settings;

          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {t('settlement.bankTransferTitle')}
                </h2>

                <p className="text-gray-600 mb-4">
                  {t('settlement.bankTransferInstructions')}
                </p>

                {/* Bank Details */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-4">
                  {/* Beneficiary Name */}
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">{t('settlement.beneficiaryName')}</p>
                      <p className="font-medium text-gray-900">{settings?.beneficiary_name}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(settings?.beneficiary_name || '', 'beneficiary')}
                      className="ml-2 p-2 text-gray-400 transition-colors"
                      onMouseEnter={(e) => e.currentTarget.style.color = brandingColors.primary}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                      title={t('common.copy')}
                    >
                      {copiedField === 'beneficiary' ? (
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* IBAN */}
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">IBAN</p>
                      <p className="font-mono font-medium text-gray-900">{formatIban(settings?.iban || '')}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(settings?.iban?.replace(/\s/g, '') || '', 'iban')}
                      className="ml-2 p-2 text-gray-400 transition-colors"
                      onMouseEnter={(e) => e.currentTarget.style.color = brandingColors.primary}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                      title={t('common.copy')}
                    >
                      {copiedField === 'iban' ? (
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* BIC */}
                  {settings?.bic && (
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">BIC/SWIFT</p>
                        <p className="font-mono font-medium text-gray-900">{settings.bic}</p>
                      </div>
                      <button
                        onClick={() => copyToClipboard(settings.bic || '', 'bic')}
                        className="ml-2 p-2 text-gray-400 transition-colors"
                        onMouseEnter={(e) => e.currentTarget.style.color = brandingColors.primary}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                        title={t('common.copy')}
                      >
                        {copiedField === 'bic' ? (
                          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Bank Name */}
                  {settings?.bank_name && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">{t('settlement.bankName')}</p>
                      <p className="font-medium text-gray-900">{settings.bank_name}</p>
                    </div>
                  )}

                  {/* Amount */}
                  <div className="flex justify-between items-start border-t border-gray-200 pt-3 mt-3">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">{t('settlement.amountLabel')}</p>
                      <p className="font-bold text-lg text-gray-900">
                        {formatCurrency(settlementData.invoice.amount_due, settlementData.invoice.currency)}
                      </p>
                    </div>
                    <button
                      onClick={() => copyToClipboard((settlementData.invoice.amount_due / 100).toFixed(2), 'amount')}
                      className="ml-2 p-2 text-gray-400 transition-colors"
                      onMouseEnter={(e) => e.currentTarget.style.color = brandingColors.primary}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                      title={t('common.copy')}
                    >
                      {copiedField === 'amount' ? (
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Payment Reference */}
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">{t('settlement.paymentReference')}</p>
                      <p className="font-mono font-bold" style={{ color: brandingColors.primary }}>{bankTransferReference}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(bankTransferReference, 'reference')}
                      className="ml-2 p-2 text-gray-400 transition-colors"
                      onMouseEnter={(e) => e.currentTarget.style.color = brandingColors.primary}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                      title={t('common.copy')}
                    >
                      {copiedField === 'reference' ? (
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Important Note about Reference */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    <strong>{t('settlement.important')}:</strong> {t('settlement.referenceImportant')}
                  </p>
                </div>

                {/* QR Code Section */}
                {settings?.qr_code_enabled && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('settlement.qrCodeTitle')}</h3>
                    <p className="text-xs text-gray-500 mb-3">{t('settlement.qrCodeDescription')}</p>

                    <div className="flex justify-center">
                      {loadingQrCode ? (
                        <div className="w-48 h-48 flex items-center justify-center bg-gray-100 rounded-lg">
                          <div
                            className="animate-spin rounded-full h-8 w-8 border-b-2"
                            style={{ borderColor: brandingColors.primary }}
                          ></div>
                        </div>
                      ) : bankTransferQrCode ? (
                        <img
                          src={bankTransferQrCode}
                          alt="Payment QR Code"
                          className="w-48 h-48 border border-gray-200 rounded-lg"
                        />
                      ) : (
                        <div className="w-48 h-48 flex items-center justify-center bg-gray-100 rounded-lg text-gray-400 text-sm">
                          {t('settlement.qrCodeUnavailable')}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Close Button */}
                <div className="mt-6">
                  <button
                    onClick={() => {
                      setShowBankTransferDialog(false);
                      setBankTransferQrCode(null);
                    }}
                    className="w-full px-4 py-3 text-white rounded-lg font-medium transition-colors"
                    style={{ backgroundColor: brandingColors.primary }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = brandingColors.primaryHover}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = brandingColors.primary}
                  >
                    {t('settlement.understood')}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>{t('settlement.poweredBy', { tenant: settlementData.tenant.tenant_name })}</p>
          <p className="mt-1">
            {t('settlement.contactQuestion', { email: settlementData.invoice.contact_info.email })}
          </p>
        </div>
      </div>
    </div>
  );
};
